<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ConversationApi from 'dashboard/api/inbox/conversation';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Icon from 'next/icon/Icon.vue';
import {
  SLOT_SETUP_TYPES,
  setupTypeLabel,
} from 'dashboard/routes/dashboard/planning/events/timeslotSetup';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    default: null,
  },
  highlighted: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['created']);

const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');

const RESULT_PAUSE_MS = 1800;

const eventId = ref(null);
const confirming = ref(false);
const proposal = ref(null);
const step = ref(null);
const analyzeDone = ref(false);
const calendarDone = ref(false);
const dialogRef = ref(null);
const title = ref('');
const selectedSpaceIds = ref([]);
const setup = ref('');
const guests = ref('');
const comment = ref('');
let flowRun = 0;

const FIELD_SELECT_CLASS =
  'reset-base h-10 w-full px-3 pr-9 text-sm font-medium rounded bg-white dark:bg-slate-800 text-n-slate-12 outline outline-1 outline-n-slate-6 border-none appearance-none';

const BLANK = '\u2014';

const pause = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const stepState = key => {
  if (key === 'analyze' && analyzeDone.value) return 'done';
  if (key === 'calendar' && calendarDone.value) return 'done';
  const order = ['analyze', 'calendar', 'form'];
  const current = order.indexOf(step.value);
  const index = order.indexOf(key);
  if (current > index) return 'done';
  if (current === index) return 'active';
  return 'pending';
};

const clashNames = computed(() => [
  ...new Set(
    (proposal.value?.allocations || []).flatMap(slot =>
      (slot.conflicts || [])
        .map(conflict => conflict.event_name)
        .filter(Boolean)
    )
  ),
]);

const calendarStepLabel = () => {
  if (!calendarDone.value)
    return t('CONVERSATION.PLANNING_INQUIRY.STEP_CALENDAR');
  if (!clashNames.value.length)
    return t('CONVERSATION.PLANNING_INQUIRY.SCHEDULE_CLEAR');
  return t('CONVERSATION.PLANNING_INQUIRY.SCHEDULE_CLASH', {
    name: clashNames.value.join(', '),
  });
};

const flowSteps = computed(() => [
  {
    key: 'analyze',
    label: analyzeDone.value
      ? t('CONVERSATION.PLANNING_INQUIRY.DETAILS_COLLECTED')
      : t('CONVERSATION.PLANNING_INQUIRY.STEP_ANALYZE'),
    state: stepState('analyze'),
    succeeded: analyzeDone.value,
  },
  {
    key: 'calendar',
    label: calendarStepLabel(),
    state: stepState('calendar'),
    succeeded: calendarDone.value,
  },
]);

const planningEnabled = computed(() =>
  isFeatureEnabled.value(currentAccountId.value, FEATURE_FLAGS.PLANNING)
);
const visible = computed(
  () => planningEnabled.value && (eventId.value || isAdmin.value)
);

const load = async () => {
  const id = props.conversationId;
  eventId.value = null;
  if (!id || !planningEnabled.value) return;

  try {
    const { data } = await ConversationApi.getPlanningInquiry(id);
    if (props.conversationId !== id) return;
    eventId.value = data.id;
  } catch {
    if (props.conversationId === id) eventId.value = null;
  }
};

watch(() => [props.conversationId, planningEnabled.value], load, {
  immediate: true,
});

const onClick = async () => {
  if (eventId.value) {
    router.push({
      name: 'planning_events_show',
      params: { eventId: eventId.value },
      query: { fromConversation: props.conversationId },
    });
    return;
  }

  flowRun += 1;
  const run = flowRun;
  step.value = 'analyze';
  analyzeDone.value = false;
  calendarDone.value = false;
  proposal.value = null;
  dialogRef.value?.open();
  store.dispatch('attributes/get').catch(() => {});
  store.dispatch('agents/get').catch(() => {});
  try {
    const { data } = await ConversationApi.createPlanningInquiry(
      props.conversationId
    );
    if (run !== flowRun) return;
    if (data.id) {
      eventId.value = data.id;
      dialogRef.value?.close();
      return;
    }
    analyzeDone.value = true;
    await nextTick();
    await pause(RESULT_PAUSE_MS);
    if (run !== flowRun) return;
    step.value = 'calendar';
    const checked = await ConversationApi.checkPlanningInquiry(
      props.conversationId,
      data
    );
    if (run !== flowRun) return;
    proposal.value = {
      ...checked.data,
      custom_attributes: checked.data.custom_attributes || {},
      allocations: (checked.data.allocations || []).map(slot => ({
        ...slot,
        custom_attributes: slot.custom_attributes || {},
      })),
    };
    title.value = proposal.value.name || '';
    comment.value = proposal.value.description || '';
    guests.value = proposal.value.pax == null ? '' : String(proposal.value.pax);
    setup.value = '';
    selectedSpaceIds.value = [
      ...new Set(proposal.value.allocations.map(slot => String(slot.space_id))),
    ];
    calendarDone.value = true;
    await nextTick();
    await pause(RESULT_PAUSE_MS);
    if (run !== flowRun) return;
    await store.dispatch('spaces/get').catch(() => {});
    if (run !== flowRun) return;
    step.value = 'form';
  } catch {
    dialogRef.value?.close();
    useAlert(t('CONVERSATION.PLANNING_INQUIRY.ERROR'));
  }
};

const spaces = computed(() => store.getters['spaces/getSpaces'] || []);

const spaceOptions = computed(() => {
  const fromStore = spaces.value
    .filter(space => space.resource_id)
    .map(space => ({ value: String(space.id), label: space.name }));
  if (fromStore.length) return fromStore;
  return (proposal.value?.spaces || []).map(space => ({
    value: String(space.id),
    label: space.name,
  }));
});

const setupOptions = SLOT_SETUP_TYPES.map(value => ({
  value,
  label: setupTypeLabel(value, t),
}));

const spaceName = spaceId =>
  spaceOptions.value.find(space => space.value === String(spaceId))?.label ||
  BLANK;

const slotTime = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return BLANK;
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const slotDay = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const scheduleDays = computed(() => {
  const groups = new Map();
  (proposal.value?.allocations || []).forEach((slot, index) => {
    const day = slotDay(slot.start_at);
    if (!groups.has(day)) groups.set(day, []);
    groups.get(day).push({ slot, index });
  });
  return [...groups.entries()].map(([day, rows]) => ({ day, rows }));
});

const removeSlot = index => {
  proposal.value.allocations.splice(index, 1);
};

const confirmCreate = async () => {
  confirming.value = true;
  try {
    const pax = guests.value === '' ? null : Number(guests.value);
    const allocations = proposal.value.allocations.map(slot => ({
      ...slot,
      accept_conflict: true,
    }));
    const { data } = await ConversationApi.confirmPlanningInquiry(
      props.conversationId,
      {
        ...proposal.value,
        name: title.value.trim() || proposal.value.name,
        description: comment.value.trim(),
        pax: Number.isFinite(pax) ? pax : proposal.value.pax,
        allocations,
      }
    );
    eventId.value = data.id;
    dialogRef.value?.close();
    emit('created', data.id);
    useAlert(t('CONVERSATION.PLANNING_INQUIRY.CREATED'));
  } catch {
    useAlert(t('CONVERSATION.PLANNING_INQUIRY.ERROR'));
  } finally {
    confirming.value = false;
  }
};
</script>

<template>
  <NextButton
    v-if="visible"
    md
    slate
    no-animation
    class="font-semibold shadow"
    :class="{
      'ring-2 ring-woot-500 ring-offset-2 dark:ring-offset-slate-900 animate-pulse':
        highlighted && !eventId,
    }"
    :icon="eventId ? 'i-lucide-calendar' : 'i-lucide-calendar-plus'"
    :label="
      eventId
        ? $t('CONVERSATION.PLANNING_INQUIRY.VIEW')
        : $t('CONVERSATION.PLANNING_INQUIRY.CREATE')
    "
    @click="onClick"
  />
  <span v-else class="hidden" />
  <Dialog
    ref="dialogRef"
    type="edit"
    :width="step === 'form' ? '5xl' : 'xl'"
    overflow-y-auto
    :title="
      step === 'form'
        ? t('PLANNING.EVENTS.NEW_EVENT.TITLE')
        : t('CONVERSATION.PLANNING_INQUIRY.PREVIEW_TITLE')
    "
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div v-if="step !== 'form'" class="flex flex-col gap-3 pb-6">
      <div
        v-for="item in flowSteps"
        :key="item.key"
        class="flex items-center gap-3 text-sm text-n-slate-12"
      >
        <span
          v-if="item.state === 'done' && item.succeeded"
          class="i-lucide-check size-4 shrink-0 text-n-teal-11"
        />
        <span
          v-else-if="item.state === 'done'"
          class="i-lucide-alert-circle size-4 shrink-0 text-n-ruby-11"
        />
        <span
          v-else
          class="size-4 rounded-full shrink-0"
          :class="
            item.state === 'active'
              ? 'border-2 border-n-brand border-t-transparent animate-spin'
              : 'border border-n-weak'
          "
        />
        <span :class="item.state === 'pending' ? 'text-n-slate-11' : ''">
          {{ item.label }}
        </span>
      </div>
    </div>
    <div v-else-if="proposal" class="flex flex-col gap-5">
      <Input
        v-model="title"
        :label="t('PLANNING.EVENTS.NEW_EVENT.FIELD_TITLE')"
        :placeholder="t('PLANNING.EVENTS.NEW_EVENT.FIELD_TITLE_PLACEHOLDER')"
      />
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('PLANNING.EVENTS.NEW_EVENT.SPACE_LABEL') }}
        </label>
        <TagMultiSelectComboBox
          v-model="selectedSpaceIds"
          :options="spaceOptions"
          :placeholder="t('PLANNING.EVENTS.NEW_EVENT.SPACE_PLACEHOLDER')"
          :search-placeholder="t('PLANNING.EVENTS.NEW_EVENT.SPACE_SEARCH')"
          :empty-state="t('PLANNING.EVENTS.NEW_EVENT.NO_SPACES')"
        />
      </div>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div class="relative flex min-w-0 flex-col gap-1">
          <label class="mb-0.5 text-sm font-medium text-n-slate-12">
            {{ t('PLANNING.SHOW.TIMESLOT_MODAL.SETUP') }}
          </label>
          <div class="relative">
            <select v-model="setup" :class="FIELD_SELECT_CLASS">
              <option value="">
                {{ t('PLANNING.SHOW.TIMESLOT_MODAL.SETUP_EMPTY') }}
              </option>
              <option
                v-for="option in setupOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <Icon
              icon="i-lucide-chevron-down"
              class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-11"
            />
          </div>
        </div>
        <Input
          v-model="guests"
          type="number"
          :label="t('PLANNING.SHOW.TIMESLOT_MODAL.GUESTS')"
          min="0"
        />
      </div>
      <TextArea
        v-model="comment"
        :label="t('PLANNING.SHOW.TIMESLOT_MODAL.COMMENT')"
        :placeholder="t('PLANNING.SHOW.TIMESLOT_MODAL.COMMENT_PLACEHOLDER')"
        :max-length="2000"
        custom-text-area-wrapper-class="!rounded !border-0 !bg-white !px-3 !py-2.5 outline outline-1 outline-offset-[-1px] outline-n-slate-6 focus-within:outline-n-brand"
        custom-text-area-class="min-h-16"
      />
      <div>
        <h3 class="mb-3 text-base font-semibold text-n-slate-12">
          {{ t('PLANNING.SHOW.SCHEDULE') }}
        </h3>
        <p v-if="!proposal.allocations?.length" class="text-sm text-n-slate-11">
          {{ t('CONVERSATION.PLANNING_INQUIRY.NO_SLOTS') }}
        </p>
        <template v-else>
          <div class="max-h-72 overflow-y-auto">
            <div
              class="sticky top-0 z-10 grid grid-cols-[1.5rem_1.5rem_10rem_repeat(5,minmax(0,1fr))_2.5rem] items-center gap-x-4 bg-n-surface-1 py-2 text-xs font-semibold text-n-slate-12"
            >
              <span class="flex justify-center">
                <span
                  class="h-4 w-1.5 rounded-full bg-n-slate-6"
                  aria-hidden="true"
                />
              </span>
              <span />
              <span class="-ml-10 truncate">{{
                t('PLANNING.SHOW.TIMESLOT')
              }}</span>
              <span class="truncate">{{
                t('PLANNING.SHOW.TIMESLOT_MODAL.START_TIME')
              }}</span>
              <span class="truncate">{{
                t('PLANNING.SHOW.TIMESLOT_MODAL.END_TIME')
              }}</span>
              <span class="truncate">{{
                t('CONVERSATION.PLANNING_INQUIRY.SPACE')
              }}</span>
              <span class="truncate">{{
                t('PLANNING.SHOW.TIMESLOT_MODAL.SETUP')
              }}</span>
              <span class="truncate">{{
                t('PLANNING.SHOW.TIMESLOT_MODAL.GUESTS')
              }}</span>
              <span />
            </div>
            <div v-for="group in scheduleDays" :key="group.day" class="mb-2">
              <div
                class="grid grid-cols-[1.5rem_1.5rem_10rem_repeat(5,minmax(0,1fr))_2.5rem] items-center gap-x-4 py-3"
              >
                <span class="flex justify-center">
                  <span
                    class="h-6 w-1.5 rounded-full bg-n-slate-6"
                    aria-hidden="true"
                  />
                </span>
                <p class="col-span-8 mb-0 text-sm font-bold text-n-slate-12">
                  {{ group.day }}
                </p>
              </div>
              <div
                v-for="row in group.rows"
                :key="row.index"
                class="grid grid-cols-[1.5rem_1.5rem_10rem_repeat(5,minmax(0,1fr))_2.5rem] items-center gap-x-4 border-t border-n-weak py-3 text-sm text-n-slate-11"
              >
                <span class="flex justify-end">
                  <span
                    class="h-8 w-1.5 translate-x-2 rounded-full bg-n-slate-6"
                    aria-hidden="true"
                  />
                </span>
                <span />
                <span class="-ml-10 truncate font-semibold text-n-slate-12">
                  {{ row.slot.title || BLANK }}
                </span>
                <span class="truncate tabular-nums">{{
                  slotTime(row.slot.start_at)
                }}</span>
                <span class="truncate tabular-nums">{{
                  slotTime(row.slot.end_at)
                }}</span>
                <span class="truncate">{{
                  row.slot.space_name || spaceName(row.slot.space_id)
                }}</span>
                <span class="truncate">{{
                  row.slot.setup ? setupTypeLabel(row.slot.setup, t) : BLANK
                }}</span>
                <span class="truncate tabular-nums">{{
                  row.slot.pax ?? BLANK
                }}</span>
                <span class="flex justify-end">
                  <NextButton
                    type="button"
                    xs
                    ghost
                    ruby
                    icon="i-lucide-trash-2"
                    :aria-label="t('PLANNING.SHOW.DELETE_SLOT')"
                    @click="removeSlot(row.index)"
                  />
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <template v-if="step === 'form'" #footer>
      <div class="flex w-full items-center justify-end gap-3">
        <NextButton
          type="button"
          faded
          slate
          :label="t('PLANNING.EVENTS.CANCEL_CREATE')"
          @click="dialogRef?.close()"
        />
        <NextButton
          type="button"
          color="blue"
          :label="t('PLANNING.EVENTS.NEW_EVENT.ADD_EVENT')"
          :is-loading="confirming"
          :disabled="confirming"
          @click="confirmCreate"
        />
      </div>
    </template>
  </Dialog>
</template>
